import { filterSuggestions } from 'braid-design-system';
import { useMemo, useState } from 'react';

/* eslint-disable @typescript-eslint/no-unused-vars */
export type Suggestions<T> = any | { message: string };

interface SuggestionHelpers<T> {
  /**
   * Search value that filters the suggestions.
   */
  searchValue: any;
  /**
   * Sets the search value.
   */
  setSearchValue: (suggestion: any) => void;
  /**
   * Resets the search value to an empty state.
   */
  clearSearchValue: () => void;
  /**
   * Suggestions filtered by the search value.
   */
  suggestions: Suggestions<T>;
}

// Suggestion to show when no entries have been found.
const noEntriesSuggestion = {
  message: 'No results found.',
};

/**
 * React hook that makes using Braids AutoSuggest component easier.
 * Provide it suggestions and it will handle the rest.
 */
export const useSuggestions = <T>(
  suggestions: Suggestions<T> = noEntriesSuggestion,
): SuggestionHelpers<T> => {
  const [searchValue, setSearchValue] = useState<any>({
    text: '',
  });

  const filteredSuggestions = useMemo(
    () => runFilter(suggestions, searchValue.text),
    [searchValue.text, suggestions],
  );

  return {
    searchValue,
    setSearchValue,
    clearSearchValue: () => setSearchValue({ text: '' }),
    suggestions: filteredSuggestions,
  };
};

const runFilter = <T>(
  suggestions: Suggestions<T>,
  searchValue: string,
): Suggestions<T> => {
  // If it's a message suggestion then return it.
  if (!Array.isArray(suggestions)) {
    return suggestions;
  }
  // Filter suggestions based on the search value text.
  const filtered = filterSuggestions(suggestions, searchValue);
  // If there are no results we will return a message.
  return filtered.length ? filtered : noEntriesSuggestion;
};
